import type { SiteConfig } from '@/types'
import { getOrigin } from '@/lib/window'

export const siteConfig: SiteConfig = {
    name: 'Bazio',
    description: 'Buy, sell and connect. Bazio gives every Telegram community its own marketplace: browse listings across communities, or run your own.',
    placeholder: 'No listings match your search yet.',
    url: getOrigin()
}

/**
 * The site's own absolute origin, identical on the server and in the browser.
 *
 * `siteConfig.url` is `window.location.origin`, so it is empty during server
 * rendering — fine for a link the browser resolves itself, useless for an
 * Open Graph image, which crawlers only ever read out of the server's HTML and
 * which must be absolute.
 *
 * This value is inlined at BUILD time from a NEXT_PUBLIC key, so both sides
 * emit the same string and hydration cannot disagree. `APP_BASE_URL` is
 * deliberately NOT read here: it is server-only, so it would inline as
 * `undefined` in the browser bundle and produce exactly that mismatch. Unset or
 * still holding the placeholder, this is null and the caller degrades honestly
 * rather than emitting a broken absolute URL.
 */
const rawPublicOrigin = process.env.NEXT_PUBLIC_APP_BASE_URL ?? ''

export const publicOrigin: string | null = !rawPublicOrigin || rawPublicOrigin === 'REPLACE_ME'
    ? null
    : rawPublicOrigin.replace(/\/+$/, '')

/**
 * Telegram handles come from secrets rather than code, so the community can
 * change bot or channel without a rebuild of these constants. Both are
 * NEXT_PUBLIC because they are public handles used for browser links, and
 * neither is sensitive. The bot token is server-only and never appears here.
 */
const rawBotUsername = process.env.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME ?? ''
const rawCommunityUrl = process.env.NEXT_PUBLIC_TELEGRAM_COMMUNITY_URL ?? ''

const isPlaceholder = (value: string): boolean => !value || value === 'REPLACE_ME'

export const telegramConfig = {
    botUsername: isPlaceholder(rawBotUsername) ? null : rawBotUsername.replace(/^@/, ''),
    communityUrl: isPlaceholder(rawCommunityUrl) ? null : rawCommunityUrl
}

/**
 * Public social profiles for the footer. Public handles only, so they live in
 * NEXT_PUBLIC values alongside the Telegram links. Any entry left unset is
 * simply not rendered, so the row never shows a dead icon.
 */
const rawInstagramUrl = process.env.NEXT_PUBLIC_INSTAGRAM_URL ?? ''

export const socialConfig = {
    telegram: isPlaceholder(rawCommunityUrl) ? null : rawCommunityUrl,
    instagram: isPlaceholder(rawInstagramUrl) ? null : rawInstagramUrl
}

/** Deep link straight into the bot, optionally into a specific command. */
export const botLink = (payload?: string): string | null => {
    if (!telegramConfig.botUsername) return null

    const base = `https://t.me/${telegramConfig.botUsername}`

    return payload ? `${base}?start=${payload}` : base
}

export const NAV_LINKS = [
    { label: 'Browse', href: '/browse' },
    { label: 'Categories', href: '/browse#categories' },
    { label: 'Sell', href: '/sell' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Safety', href: '/safety' }
]

/**
 * Footer navigation. Every entry is a DEDICATED page in `src/pages`, never a
 * filtered listing view: an informational footer link has to answer the
 * question it asks. `SiteFooter` is the source of truth for what renders; this
 * mirror exists for any other surface that needs the same set.
 */
export const FOOTER_LINKS = {
    platform: [
        { label: 'How Bazio works', href: '/how-it-works' },
        { label: 'Marketplace', href: '/browse' },
        { label: 'Telegram Marketplaces', href: '/telegram-groups' },
        { label: 'For communities', href: '/for-communities' },
        { label: 'Pricing', href: '/pricing' }
    ],
    trust: [
        { label: 'Safety', href: '/safety' },
        { label: 'Report a listing', href: '/report' },
        { label: 'Contact', href: '/contact' }
    ],
    legal: [
        { label: 'Terms & Conditions', href: '/terms' },
        { label: 'Privacy Policy', href: '/privacy' }
    ]
}
